import {List, Map, Record} from 'immutable';
import isObject from 'lodash/isObject';
import {
  updatePropertyIfNeeded,
  updateIfNeeded,
} from '@packages/utilities/POJORecordUtils';
import {EventTypeConsts} from '@packages/systems/ix2/shared-constants';
import {IX2StateRecord} from './state';
import {isQuickEffect} from '@packages/systems/ix2/shared-utils';

import {
  TimedActionListRecord,
  TimedActionItemGroupRecord,
  ContinuousMouseActionListRecord,
  ContinuousScrollActionListRecord,
  ContinuousParameterGroupRecordConsts,
  ContinuousActionGroupRecord,
} from './animation-actionlist-immutable';

import {
  SelectorRecord,
  AffectedElementRecord,
  EventTargetRecordConsts,
  EventConfigRecordConsts,
  EventRecords,
} from './trigger-events-immutable';

import {InteractionRecords} from './trigger-interactions-immutable';

import {
  FontVariationItemConfigRecord,
  FilterItemConfigRecord,
  ActionConfigTargetRecord,
  ActionRecords,
} from './animation-actions-immutable';

const CONTINUOUS_PARAMETER_RECORD = {
  MOUSE_X: ContinuousMouseActionListRecord,
  MOUSE_Y: ContinuousMouseActionListRecord,
  SCROLL_PROGRESS: ContinuousScrollActionListRecord,
} as const;

export function reifyIx2State(state: any): any {
  if (isObject(state)) {
    switch (true) {
      // Case 1: All generic Lists such as events, actionLists, actionItemGroups, etc
      case Array.isArray(state): {
        return state.reduce(filterInvalidState, List());
      }

      // Case 2: Timed action list record
      // @ts-expect-error - TS2339 - Property 'actionItemGroups' does not exist on type 'object'.
      case state?.actionItemGroups != null: {
        return TimedActionListRecord(
          updatePropertyIfNeeded(
            state,
            // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
            'actionItemGroups',
            // @ts-expect-error - TS2339 - Property 'actionItemGroups' does not exist on type 'object'.
            reifyIx2State(state.actionItemGroups)
          )
        );
      }

      // Case 3: Timed action group record (keyframe is only on continuous)
      // @ts-expect-error - TS2339 - Property 'actionItems' does not exist on type 'object'. | TS2339 - Property 'keyframe' does not exist on type 'object'.
      case state?.actionItems != null && state?.keyframe == null: {
        return TimedActionItemGroupRecord(
          updatePropertyIfNeeded(
            state,
            // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
            'actionItems',
            // @ts-expect-error - TS2339 - Property 'actionItems' does not exist on type 'object'.
            reifyIx2State(state.actionItems)
          )
        );
      }

      // Case 4: Continuous action list record
      // @ts-expect-error - TS2339 - Property 'continuousParameterGroups' does not exist on type 'object'.
      case state?.continuousParameterGroups != null: {
        // @ts-expect-error - TS2339 - Property 'continuousParameterGroups' does not exist on type 'object'.
        const type = state.continuousParameterGroups?.[0]?.type;
        const ContinuousParameterRecord =
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly MOUSE_X: Factory<ContinuousMouseActionList>; readonly MOUSE_Y: Factory<ContinuousMouseActionList>; readonly SCROLL_PROGRESS: Factory<...>; }'.
          CONTINUOUS_PARAMETER_RECORD[type] ||
          CONTINUOUS_PARAMETER_RECORD.MOUSE_X;

        if (ContinuousParameterRecord != null) {
          return ContinuousParameterRecord(
            updatePropertyIfNeeded(
              state,
              // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
              'continuousParameterGroups',
              // @ts-expect-error - TS2339 - Property 'continuousParameterGroups' does not exist on type 'object'.
              reifyIx2State(state.continuousParameterGroups)
            )
          );
        }

        break;
      }

      // Case 5: Continuous parameter group record
      // @ts-expect-error - TS2339 - Property 'continuousActionGroups' does not exist on type 'object'. | TS2339 - Property 'type' does not exist on type 'object'.
      case state?.continuousActionGroups != null && state?.type != null: {
        const IX2ContinuousParameterGroup =
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ MOUSE_X: Factory<ContinuousParameterGroupsMouseX>; MOUSE_Y: Factory<ContinuousParameterGroupsMouseY>; SCROLL_PROGRESS: Factory<...>; }'. | TS2339 - Property 'type' does not exist on type 'object'.
          ContinuousParameterGroupRecordConsts[state.type];

        if (IX2ContinuousParameterGroup != null) {
          return IX2ContinuousParameterGroup(
            updatePropertyIfNeeded(
              state,
              // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
              'continuousActionGroups',
              // @ts-expect-error - TS2339 - Property 'continuousActionGroups' does not exist on type 'object'.
              reifyIx2State(state.continuousActionGroups)
            )
          );
        }

        break;
      }

      // Case 6: Continuous action group record
      // @ts-expect-error - TS2339 - Property 'keyframe' does not exist on type 'object'.
      case state?.keyframe != null: {
        return ContinuousActionGroupRecord(
          updatePropertyIfNeeded(
            state,
            // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
            'actionItems',
            // @ts-expect-error - TS2339 - Property 'actionItems' does not exist on type 'object'.
            reifyIx2State(state.actionItems)
          )
        );
      }

      // Case 7: Action item / config — covers event action as well
      // @ts-expect-error - TS2339 - Property 'actionTypeId' does not exist on type 'object'.
      case state?.actionTypeId != null: {
        // @ts-expect-error - TS2339 - Property 'config' does not exist on type 'object'.
        if (state.config?.affectedElements != null) {
          state = updatePropertyIfNeeded(
            state,
            // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
            'config',
            updatePropertyIfNeeded(
              // @ts-expect-error - TS2339 - Property 'config' does not exist on type 'object'.
              state.config,
              'affectedElements',
              // @ts-expect-error - TS2339 - Property 'config' does not exist on type 'object'.
              affectedElementsToMapRecord(state.config.affectedElements)
            )
          );
        }

        if (state?.config?.filters != null) {
          state = updatePropertyIfNeeded(
            state,
            'config',
            updatePropertyIfNeeded(
              state.config,
              'filters',
              List(state.config.filters.map(FilterItemConfigRecord))
            )
          );
        }

        if (state?.config?.fontVariations != null) {
          state = updatePropertyIfNeeded(
            state,
            'config',
            updatePropertyIfNeeded(
              state.config,
              'fontVariations',
              List(
                state.config.fontVariations.map(FontVariationItemConfigRecord)
              )
            )
          );
        }

        return createActionFactory(state);
      }

      // Case 8: Event
      // @ts-expect-error - TS2339 - Property 'eventTypeId' does not exist on type 'object'.
      case state?.eventTypeId != null: {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly DROPDOWN_OPEN: Factory<{ config: Record<TimedEventConfigType> & Readonly<TimedEventConfigType>; target: Record<EventTargetElementType & { ...; }> & Readonly<...>; ... 7 more ...; createdOn: number | null; }>; ... 20 more ...; readonly PAGE_SCROLL: Factory<...>; }'. | TS2339 - Property 'eventTypeId' does not exist on type 'object'.
        const IX2Event = EventRecords[state.eventTypeId];
        // @ts-expect-error - TS2339 - Property 'animationType' does not exist on type 'object'.
        let animationType = state.animationType;

        if (animationType == null) {
          // @ts-expect-error - TS2339 - Property 'action' does not exist on type 'object'.
          animationType = isQuickEffect(state?.action?.actionTypeId)
            ? 'preset'
            : 'custom';
        }

        if (IX2Event != null) {
          return IX2Event({
            ...state,
            // @ts-expect-error - TS2339 - Property 'name' does not exist on type 'object'. | TS2339 - Property 'name' does not exist on type 'object'.
            name: state.name === 'Unnamed interaction' ? '' : state.name,
            animationType,
            // @ts-expect-error - TS2339 - Property 'target' does not exist on type 'object'.
            target: reifyEventTarget(state.target),
            targets: List(
              // @ts-expect-error - TS2339 - Property 'targets' does not exist on type 'object'. | TS2339 - Property 'targets' does not exist on type 'object'.
              state.targets == null || state.targets.length === 0
                ? // @ts-expect-error - TS2339 - Property 'target' does not exist on type 'object'.
                  [state.target]
                : // @ts-expect-error - TS2339 - Property 'targets' does not exist on type 'object'.
                  state.targets
            ).map(reifyEventTarget),
            config:
              // @ts-expect-error - TS2339 - Property 'config' does not exist on type 'object'. | TS2339 - Property 'config' does not exist on type 'object'.
              Array.isArray(state.config) && state.config.length
                ? // @ts-expect-error - TS2339 - Property 'config' does not exist on type 'object'.
                  reifyIx2State(state.config)
                : // @ts-expect-error - TS2339 - Property 'config' does not exist on type 'object'. | TS2339 - Property 'eventTypeId' does not exist on type 'object'.
                  reifyEventTimedConfig(state.config, state.eventTypeId),
            // @ts-expect-error - TS2339 - Property 'action' does not exist on type 'object'. | TS2339 - Property 'action' does not exist on type 'object'.
            action: state.action ? createActionFactory(state.action) : null,
          });
        }

        break;
      }

      // Case 9: Interaction
      // @ts-expect-error - TS2339 - Property 'interactionTypeId' does not exist on type 'object'.
      case state?.interactionTypeId != null: {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly MOUSE_CLICK_INTERACTION: Factory<MouseClickIXType>; readonly MOUSE_HOVER_INTERACTION: Factory<MouseHoverIXType>; ... 10 more ...; readonly TAB_INTERACTION: Factory<...>; }'. | TS2339 - Property 'interactionTypeId' does not exist on type 'object'.
        const InteractionRecord = InteractionRecords[state.interactionTypeId];

        if (InteractionRecord != null) {
          return InteractionRecord(state);
        }

        break;
      }

      // Case 10: Mouse event config
      // @ts-expect-error - TS2339 - Property 'basedOn' does not exist on type 'object'. | TS2339 - Property 'selectedAxis' does not exist on type 'object'.
      case state?.basedOn != null && state?.selectedAxis != null: {
        // @ts-expect-error - TS2339 - Property 'basedOn' does not exist on type '{}'. | TS2339 - Property 'selectedAxis' does not exist on type '{}'.
        const {basedOn, selectedAxis} = state;
        const MouseConfigRecord =
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ ELEMENT: { X_AXIS: Factory<ContinuousEventMouseConfigType<"X_AXIS", "ELEMENT">>; Y_AXIS: Factory<ContinuousEventMouseConfigType<"Y_AXIS", "ELEMENT">>; }; VIEWPORT: { ...; }; }'.
          EventConfigRecordConsts.CONTINUOUS.MOUSE[basedOn][selectedAxis];

        if (MouseConfigRecord) {
          return MouseConfigRecord(state);
        }

        break;
      }

      // Case 11: Scroll event config
      // @ts-expect-error - TS2339 - Property 'startsEntering' does not exist on type 'object'.
      case state?.startsEntering != null: {
        return EventConfigRecordConsts.CONTINUOUS.SCROLL(state);
      }

      // Kick off!
      // @ts-expect-error - TS2339 - Property 'actionLists' does not exist on type 'object'. | TS2339 - Property 'events' does not exist on type 'object'.
      case state.actionLists != null && state.events != null: {
        return IX2StateRecord({
          // @ts-expect-error - TS2339 - Property 'actionLists' does not exist on type 'object'.
          actionLists: reifyIx2State(state.actionLists),
          // @ts-expect-error - TS2339 - Property 'events' does not exist on type 'object'.
          events: reifyIx2State(state.events),
          // @ts-expect-error - TS2339 - Property 'interactions' does not exist on type 'object'.
          interactions: reifyIx2State(state.interactions),
          // @ts-expect-error - TS2339 - Property 'baseActionListIds' does not exist on type 'object'.
          baseActionListIds: state.baseActionListIds,
          // @ts-expect-error - TS2339 - Property 'baseEventIds' does not exist on type 'object'.
          baseEventIds: state.baseEventIds,
          // @ts-expect-error - TS2339 - Property 'baseInteractionIds' does not exist on type 'object'.
          baseInteractionIds: state.baseInteractionIds,
          // @ts-expect-error - TS2339 - Property 'branchId' does not exist on type 'object'.
          branchId: state.branchId,
        });
      }
    }
  }
}

function filterInvalidState(valid: any, state: any) {
  const reifiedItem = reifyIx2State(state);
  return reifiedItem instanceof Record // Remove unexpected values and null
    ? valid.push(reifiedItem)
    : valid;
}

function affectedElementsToMapRecord(affectedElements: any) {
  // @ts-expect-error - TS2314 - Generic type 'Record<TProps>' requires 1 type argument(s).
  const map: Record<string, any> = {};

  for (const nodeId in affectedElements) {
    const {selector, limitAffectedElements} = affectedElements[nodeId];

    if (selector == null) {
      map[nodeId] = AffectedElementRecord();
      continue;
    }

    map[nodeId] = AffectedElementRecord({
      // @ts-expect-error - TS2322 - Type 'Record<{ selectorGuids: List<string>; }> & Readonly<{ selectorGuids: List<string>; }>' is not assignable to type '({ selectorGuids: string[]; } & Record<{ selectorGuids: List<string>; }> & Readonly<{ selectorGuids: List<string>; }>) | undefined'.
      selector: SelectorRecord({
        selectorGuids: List(selector.selectorGuids),
      }),
      limitAffectedElements,
    });
  }

  return Map(map);
}

function reifyEventTarget(eventTarget: any) {
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ ELEMENT: Factory<EventTargetElementType & { styleBlockIds: List<string>; }>; PAGE: Factory<EventTargetPageType & { ...; }>; CLASS: Factory<...>; }'.
  const IX2EventTarget = EventTargetRecordConsts[eventTarget.appliesTo];

  if (IX2EventTarget != null) {
    return IX2EventTarget(
      updatePropertyIfNeeded(
        eventTarget,
        'styleBlockIds',
        List(eventTarget.styleBlockIds || [])
      )
    );
  } else {
    return IX2EventTarget();
  }
}

const defaultScrollOffsetValues = {
  scrollOffsetUnit: '%',
  scrollOffsetValue: 0,
} as const;

const EVENTS_NEEDING_DEFAULT_SCROLL_OFFSET_VALUES = [
  EventTypeConsts.SCROLL_OUT_OF_VIEW,
  EventTypeConsts.SCROLL_INTO_VIEW,
  EventTypeConsts.PAGE_SCROLL_UP,
  EventTypeConsts.PAGE_SCROLL_DOWN,
];

// @ts-expect-error - TS7006 - Parameter 'config' implicitly has an 'any' type.
function reifyEventTimedConfig(config, eventTypeId: any) {
  config = config || {};

  if (EVENTS_NEEDING_DEFAULT_SCROLL_OFFSET_VALUES.includes(eventTypeId)) {
    // This is intentially backwards — this fixes a legacy bug where old
    // IX2 event configs did _not_ have the proper offset value and unit
    // so we had to retroactively add it if the config didn't have it
    config = updateIfNeeded(defaultScrollOffsetValues, config);
  }

  return EventConfigRecordConsts.TIMED(config);
}

function createActionFactory(state: any) {
  // HACK Hotfix for a bug where some sites that were testing lottie prior to beta had this old
  // actionTypeId, which was updated to PLUGIN_LOTTIE, causing the designer to crash on load
  if (state.actionTypeId === 'PLUGIN_BODYMOVIN') {
    state = {...state, actionTypeId: 'PLUGIN_LOTTIE'};
  }

  const {config, ...action} = state;
  const {target, ...restConfig} = config;
  let affectedElements = null;

  if (config.affectedElements != null) {
    affectedElements = affectedElementsToMapRecord(config.affectedElements);
  }

  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly TRANSFORM_MOVE: Factory<MoveActionType & { delay: number; easing: IX2EasingType; duration: number; target: ActionItemTargetType; ... 5 more ...; zUnit: "%" | ... 4 more ... | "VW"; }>; ... 11 more ...; readonly PLUGIN_SPLINE: Factory<...>; }'.
  if (typeof ActionRecords[state.actionTypeId] === 'function') {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly TRANSFORM_MOVE: Factory<MoveActionType & { delay: number; easing: IX2EasingType; duration: number; target: ActionItemTargetType; ... 5 more ...; zUnit: "%" | ... 4 more ... | "VW"; }>; ... 11 more ...; readonly PLUGIN_SPLINE: Factory<...>; }'.
    const actionItemFactory = ActionRecords[state.actionTypeId];

    // @ts-expect-error - TS7006 - Parameter 'configRecord' implicitly has an 'any' type.
    return actionItemFactory(action).update('config', (configRecord) => {
      let reifiedConfig = configRecord.merge(restConfig);

      if (target) {
        // @ts-expect-error - TS7006 - Parameter 'targetRecord' implicitly has an 'any' type.
        reifiedConfig = reifiedConfig.update('target', (targetRecord) =>
          // `target` used to be a string in the original IX2 launch, but as
          // we added more targeting options, we updated the data structure
          // to be an object. This code updates modernizes legacy IX2 to use
          // the updated data structure
          {
            if (typeof target === 'string') {
              targetRecord = targetRecord.set('nodeId', target);
            } else if (typeof target?.id === 'string') {
              targetRecord = targetRecord.set('nodeId', target?.id);
            } else {
              targetRecord = targetRecord.merge({
                ...target,
                styleBlockIds: List(target.styleBlockIds || []),
              });
            }

            return targetRecord;
          }
        );
      }

      return reifiedConfig;
    });
  }

  const actionConfigTarget = target
    ? ActionConfigTargetRecord({
        ...(typeof target === 'string' ? {nodeId: target} : target),
        styleBlockIds: List(target.styleBlockIds || []),
      })
    : null;

  const actionConfigShape = {...restConfig} as const;
  if (actionConfigTarget) actionConfigShape.target = actionConfigTarget;
  if (affectedElements) actionConfigShape.affectedElements = affectedElements;
  const actionConfig = Record(actionConfigShape, 'EventActionConfigRecord')();

  // Handles event.action data structure
  return Record({...action, config: actionConfig}, 'EventActionRecord')();
}
